/**
 * HARRY'S OWN HAIR WEIGHTS — ADR-039 T1 (Allen, 2026-09-30: once a region is
 * confirmed as hair, the generator's weights there are discarded and Harry
 * assigns its own). Tripo's are nearest-bone accidents: the realistic-spray
 * specimen paints L_Upperarm on 3,112 of its hair vertices, and Shirley's
 * clavicles DOMINATE 996 of hers at weights up to 0.86 (probe brief,
 * "Generator contamination").
 *
 * T1's rule, the whole of it: every hair vertex keeps only its Head-subtree
 * influences, renormalised; a vertex with none goes to the Head outright. The
 * Head is the root every hair treatment hangs from (J4 rides it; T2's chains
 * root on it). The largest kept influence takes the float32 remainder; when T2
 * writes these into a file, the export door's exact-sum law (LAWS: the file
 * must survive its own loader) governs, not this.
 *
 * T1 COMPUTES AND REPORTS; IT DOES NOT APPLY. The result is recorded on the
 * Hair card's status line and the app probe; the run and the export door are
 * untouched until T2, because the seam this leaves at the region's border
 * (clavicle-weighted skin beside Head-only hair) and Shirley's open question
 * (does a bob resting on the shoulders hang off the Head alone?) are T2's to
 * answer with the physics that rides on them (ADR-039 §5).
 *
 * T2a (ADR-039 §13) APPLIES them, for confirmed ATTACHED hair only, through
 * `hair-soften.ts`: feathered into the inherited paint at the region's border.
 *
 * Depends on: nothing. Depended on by: the app's Hair tool and the kernel tests.
 */

export interface HairWeightsInput {
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> };
  /** Skin-order bone names and parents (the subtree is read from them). */
  readonly bones: readonly { readonly name: string; readonly parent: string | null }[];
  readonly headBone: string;
  /** The confirmed region's vertices (raw indices, twins included). */
  readonly vertices: ArrayLike<number>;
}

export interface HairWeightsResult {
  /** Full copies of the skin; only the region's vertices differ from the input. */
  readonly index: Uint32Array;
  readonly weight: Float32Array;
  /** Region vertices whose influences changed. */
  readonly changed: number;
  /** Region vertices that had no Head-subtree influence at all and now belong to the Head outright. */
  readonly toHead: number;
  /** Per stripped bone: region vertices it lost, and the weight it carried there (summed). */
  readonly stripped: readonly { readonly bone: string; readonly vertices: number; readonly mass: number }[];
}

export class HairWeightsRefusal extends Error {}

export function hairWeights(input: HairWeightsInput): HairWeightsResult {
  const { skin, bones } = input;
  const head = bones.findIndex((b) => b.name === input.headBone);
  if (head < 0) throw new HairWeightsRefusal(`hair weights: no bone "${input.headBone}" in the skin`);
  const byName = new Map(bones.map((b, i) => [b.name, i]));
  const sub = new Set<number>([head]);
  for (let grew = true; grew; ) {
    grew = false;
    bones.forEach((b, i) => {
      if (!sub.has(i) && b.parent !== null && sub.has(byName.get(b.parent) ?? -1)) {
        sub.add(i);
        grew = true;
      }
    });
  }
  const index = Uint32Array.from(skin.index as ArrayLike<number>);
  const weight = Float32Array.from(skin.weight as ArrayLike<number>);
  const stripped = new Map<number, { vertices: number; mass: number }>();
  let changed = 0;
  let toHead = 0;
  const seen = new Set<number>();
  for (let q = 0; q < input.vertices.length; q++) {
    const v = input.vertices[q]!;
    if (seen.has(v)) continue;
    seen.add(v);
    let keep = 0;
    let moved = false;
    for (let k = 0; k < 4; k++) {
      const b = index[v * 4 + k]!;
      const w = weight[v * 4 + k]!;
      if (w <= 0) continue;
      if (sub.has(b)) keep += w;
      else {
        moved = true;
        const s = stripped.get(b) ?? { vertices: 0, mass: 0 };
        s.vertices++;
        s.mass += w;
        stripped.set(b, s);
      }
    }
    if (!moved) continue;
    changed++;
    if (keep <= 0) {
      toHead++;
      for (let k = 0; k < 4; k++) {
        index[v * 4 + k] = k === 0 ? head : 0;
        weight[v * 4 + k] = k === 0 ? 1 : 0;
      }
      continue;
    }
    // keep the Head-subtree influences, renormalised; exact float32 sum (the largest takes the remainder)
    let largest = -1;
    let sum = 0;
    for (let k = 0; k < 4; k++) {
      const b = index[v * 4 + k]!;
      const w = weight[v * 4 + k]!;
      if (w <= 0 || !sub.has(b)) {
        index[v * 4 + k] = 0;
        weight[v * 4 + k] = 0;
        continue;
      }
      weight[v * 4 + k] = Math.fround(w / keep);
      if (largest < 0 || weight[v * 4 + k]! > weight[v * 4 + largest]!) largest = k;
    }
    for (let k = 0; k < 4; k++) if (k !== largest) sum = Math.fround(sum + weight[v * 4 + k]!);
    weight[v * 4 + largest] = Math.fround(1 - sum);
  }
  return {
    index,
    weight,
    changed,
    toHead,
    stripped: [...stripped]
      .map(([b, s]) => ({ bone: bones[b]?.name ?? `#${b}`, vertices: s.vertices, mass: s.mass }))
      .sort((a, b) => b.mass - a.mass || a.bone.localeCompare(b.bone)),
  };
}
